import { SERVICE_CATEGORIES } from '@powerful/config/categories';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { clearFilters, FilterPanel, FilterSheet } from '../../components/discovery/filter-sheet';
import { SearchBar } from '../../components/discovery/search-bar';
import { SearchFeed } from '../../components/discovery/search-feed';
import { SortMenu } from '../../components/discovery/sort-menu';
import { Icon } from '../../components/icons/icon';
import { useSearch } from '../../hooks/use-search';
import { countActiveFilters, type DiscoveryFilters } from '../../lib/discovery/search-params';
import { useDiscoveryFilters, withLocationCity } from '../../lib/discovery/use-discovery-filters';
import { useDiscoveryLocation } from '../../lib/geo/discovery-location';

/**
 * Resultados da busca — canvas T2.1 `Results`/`ResultsFilters`/`ResultsEmpty` (F3).
 *
 * Todo o estado vive na URL (C2): termo, filtros e ordenação. Aplicar gera UMA entrada no
 * histórico, e Back/Forward reconstroem a tela a partir dela. O contador é de filtros ATIVOS e de
 * resultados CARREGADOS — a API devolve `total:null`, e «48 resultados» seria número inventado.
 */
export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location, status, openPicker } = useDiscoveryLocation();
  const { filters, apply } = useDiscoveryFilters();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const locationPending = status === 'loading';
  const origin = location?.origin;
  const hasOrigin = Boolean(origin);
  const search = useSearch(filters, origin, { enabled: !locationPending });
  const active = countActiveFilters(filters);

  const go = (next: DiscoveryFilters) => apply(withLocationCity(next, location));
  const addCategory = (id: string) => {
    const categoryIds = [...new Set([...(filters.categoryIds ?? []), id])];
    const next: DiscoveryFilters = { ...filters, categoryIds };
    delete next.query;
    go(next);
  };

  const singleCategory = filters.categoryIds?.length === 1 ? SERVICE_CATEGORIES.find((c) => c.id === filters.categoryIds![0]) : undefined;
  const title = filters.query
    ? t('discovery.results.titleQuery', { query: filters.query })
    : singleCategory
      ? t(`onboarding.categories.${singleCategory.slug}`)
      : t('discovery.results.title');
  const place = location?.city?.name ?? (location ? t('discovery.location.device') : null);

  return (
    <div className="pc-main" data-testid="page-search" data-screen-label="search">
      <div className="pc-split">
        <FilterPanel value={filters} onApply={go} withCategories hasOrigin={hasOrigin} onChooseLocation={openPicker} />
        <div className="pc-stack gap-5">
          <div className="flex min-w-0 items-center gap-1">
            <Link className="hit pc-iconbtn -ml-[10px]" to="/home" aria-label={t('discovery.results.backHome')}>
              <Icon name="chevLeft" size={22} strokeWidth={2} />
            </Link>
            <h1 className="pc-h2 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" data-testid="results-title">{title}</h1>
          </div>

          <div className="pc-sec">
            <label className="pc-vh" id="search-q-label" htmlFor="search-q">{t('actions.search')}</label>
            <SearchBar
              labelledBy="search-q-label"
              inputId="search-q"
              initialValue={filters.query ?? ''}
              onSubmitText={(text) => {
                const next = { ...filters };
                if (text) next.query = text;
                else delete next.query;
                go(next);
              }}
              onSelectCategory={addCategory}
              onSelectBusiness={(slug) => navigate(`/pro/${encodeURIComponent(slug)}`)}
            />
          </div>

          <div className="pc-toolbar">
            <p className="pc-muted min-w-0" aria-live="polite" data-testid="results-count">
              {search.loadedCount > 0 ? (
                <>
                  <strong className="text-ink-1">
                    {search.hasMore
                      ? t('discovery.results.showingFirst', { count: search.loadedCount })
                      : t('discovery.results.showing', { count: search.loadedCount })}
                  </strong>
                  {place ? ` ${t('discovery.results.near', { place })}` : null}
                </>
              ) : null}
            </p>
            <div className="pc-row">
              <button
                className="btn btn-ghost pc-only-mobile"
                type="button"
                aria-haspopup="dialog"
                onClick={() => setSheetOpen(true)}
                data-testid="filters-button"
              >
                <Icon name="sliders" size={18} />
                <span>{t('discovery.filters.open')}</span>
                {active > 0 ? (
                  <span className="pc-count">
                    <span aria-hidden="true">{active}</span>
                    <span className="pc-vh">{t('discovery.filters.activeCount', { count: active })}</span>
                  </span>
                ) : null}
              </button>
              <SortMenu value={filters.sort ?? 'relevance'} hasOrigin={hasOrigin} onChange={(sort) => go({ ...filters, sort })} />
            </div>
          </div>

          <div className="pc-sec-head">
            <span className="pc-muted">{t('discovery.sort.current', { sort: t(`discovery.sort.options.${filters.sort ?? 'relevance'}`) })}</span>
            <button className="hit pc-textbtn" type="button" aria-haspopup="dialog" onClick={() => setHowOpen(true)}>
              <Icon name="info" size={16} />
              <span>{t('discovery.ranking.how')}</span>
            </button>
          </div>

          <SearchFeed
            search={search}
            hasOrigin={hasOrigin}
            locationPending={locationPending}
            label={t('discovery.results.label')}
            emptyKind={active > 0 || filters.query ? 'filters' : 'zone'}
            onChooseCity={openPicker}
            onClearFilters={() => go(clearFilters({ ...filters, query: undefined }))}
            onWiden={hasOrigin && filters.radiusKm !== undefined && filters.radiusKm < 50 ? () => go({ ...filters, radiusKm: 50 }) : undefined}
            howOpen={howOpen}
            onHowClose={() => setHowOpen(false)}
          />
        </div>
      </div>

      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        value={filters}
        onApply={go}
        hasOrigin={hasOrigin}
        onChooseLocation={openPicker}
      />
    </div>
  );
}
